import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Shield, Lock, User, Building2, FileCheck, CheckCircle, AlertTriangle, ChevronRight, ChevronLeft } from 'lucide-react';

const STEPS = [
  { id: 0, label: 'Entry Gate', icon: Shield, description: 'Consent & Authorization' },
  { id: 1, label: 'Identity', icon: User, description: 'Personal Information' },
  { id: 2, label: 'Verification', icon: Lock, description: 'Identity Assurance (IAL2)' },
  { id: 3, label: 'Role & Clearance', icon: Building2, description: 'Access Classification' },
  { id: 4, label: 'Review', icon: FileCheck, description: 'Compliance Review' },
  { id: 5, label: 'Complete', icon: CheckCircle, description: 'Access Provisioned' },
];

const AGENCIES = [
  "Department of the Treasury", "Department of Defense",
  "Office of Personnel Management", "Department of State",
  "Department of Justice", "Department of Homeland Security",
  "Department of Veterans Affairs", "Department of Energy",
  "General Services Administration", "Other Federal Agency"
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME",
  "MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI",
  "SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

export const OnboardingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [onboardingId, setOnboardingId] = useState(null);

  // Phase 0
  const [consent, setConsent] = useState(false);
  const [monitoring, setMonitoring] = useState(false);

  // Phase 1
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [agency, setAgency] = useState('');

  // Phase 2
  const [ssnLast4, setSsnLast4] = useState('');
  const [dob, setDob] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [docType, setDocType] = useState('drivers_license');

  // Phase 3
  const [role, setRole] = useState('federal_employee');
  const [subAgency, setSubAgency] = useState('');
  const [clearance, setClearance] = useState('none');
  const [positionTitle, setPositionTitle] = useState('');

  useEffect(() => {
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get('/onboarding/status');
      const data = res.data;
      if (data.state === 'ACCESS_GRANTED') {
        navigate('/dashboard');
        return;
      }
      if (data.id) {
        setOnboardingId(data.id);
        const phase = data.current_phase || 0;
        if (phase >= 5) setCurrentStep(5);
        else if (phase >= 3) setCurrentStep(3);
        else if (phase >= 2) setCurrentStep(2);
        else if (phase >= 1) setCurrentStep(1);
      }
    } catch {}
  };

  const handleInitialize = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/onboarding/initialize');
      setOnboardingId(res.data.id);
      setCurrentStep(1);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initialize');
    } finally {
      setLoading(false);
    }
  };

  const handlePhase1 = async () => {
    if (!fullName.trim() || !phone.trim() || !agency) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/onboarding/phase1', {
        full_legal_name: fullName,
        phone_number: phone,
        agency_department: agency,
        consent_acknowledged: consent,
        monitoring_disclosed: monitoring
      });
      setCurrentStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const handlePhase2 = async () => {
    if (!ssnLast4 || ssnLast4.length !== 4 || !dob || !address1 || !city || !state || !zip) {
      setError('All required fields must be completed');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/onboarding/phase2', {
        ssn_last_four: ssnLast4,
        date_of_birth: dob,
        address_line1: address1,
        address_line2: address2,
        city, state, zip_code: zip,
        id_document_type: docType
      });
      setCurrentStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const handlePhase3 = async () => {
    if (!role || !agency) {
      setError('Role and agency are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/onboarding/phase3', {
        role, agency, sub_agency: subAgency,
        clearance_level: clearance, position_title: positionTitle
      });
      // Submit for review + provision access in sequence
      await api.post('/onboarding/submit-review');
      await api.post('/onboarding/provision-access');
      setCurrentStep(5);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full h-11 px-4 bg-white border border-[#D1D5DB] rounded text-[#1B2A4A] text-sm focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20 transition-all";
  const selectClass = "w-full h-11 px-4 bg-white border border-[#D1D5DB] rounded text-[#1B2A4A] text-sm focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20 transition-all appearance-none";
  const labelClass = "block text-sm font-medium text-[#1B2A4A] mb-1.5";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E5E9]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/unifed-logo-transparent.png" alt="UniFed One" className="h-10 w-auto" data-testid="onboarding-logo" />
            <div>
              <h1 className="text-base font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>UniFed One</h1>
              <span className="text-[10px] text-[#6B7280] uppercase tracking-wider font-mono">Secure Onboarding</span>
            </div>
          </div>
          <span className="text-xs text-[#6B7280] font-mono">{user?.email}</span>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white border-b border-[#E2E5E9]">
        <div className="max-w-4xl mx-auto px-6 py-3">
          <div className="flex items-center gap-1 overflow-x-auto" data-testid="onboarding-progress">
            {STEPS.map((step, i) => {
              const isActive = i === currentStep;
              const isComplete = i < currentStep;
              const StepIcon = step.icon;
              return (
                <div key={step.id} className="flex items-center shrink-0">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    isComplete ? 'bg-emerald-50 text-emerald-700' :
                    isActive ? 'bg-[#1B2A4A] text-white' :
                    'bg-gray-50 text-[#9CA3AF]'
                  }`}>
                    <StepIcon size={14} />
                    <span className="hidden sm:inline">{step.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <ChevronRight size={14} className="text-[#D1D5DB] mx-1" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 flex items-start gap-2 bg-red-50 border border-red-200 rounded px-4 py-3" data-testid="onboarding-error">
            <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Step 0: Entry Gate */}
        {currentStep === 0 && (
          <div className="space-y-6" data-testid="phase-0">
            <div>
              <h2 className="text-xl font-semibold text-[#1B2A4A] mb-1" style={{ fontFamily: 'IBM Plex Sans' }}>Secure Entry Gate</h2>
              <p className="text-sm text-[#6B7280]">Review and acknowledge the following before proceeding.</p>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 px-4 py-3">
              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">U.S. Government System — Authorized Use Only</p>
              <p className="text-xs text-amber-700">
                This system is for authorized U.S. Government use only. Unauthorized access is prohibited and subject to criminal prosecution under 18 U.S.C. § 1030.
              </p>
            </div>

            <div className="bg-white border border-[#E2E5E9] rounded p-5 space-y-4">
              <div className="flex items-start gap-3">
                <input type="checkbox" id="consent" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 text-[#1B2A4A] border-gray-300 rounded focus:ring-[#1B2A4A]"
                  data-testid="consent-checkbox" />
                <label htmlFor="consent" className="text-sm text-[#374151]">
                  <span className="font-medium">Consent to Information Collection:</span> I acknowledge that information collected during this onboarding process will be used for identity verification under federal compliance requirements, including NIST SP 800-63-3 Digital Identity Guidelines.
                </label>
              </div>
              <div className="flex items-start gap-3">
                <input type="checkbox" id="monitoring" checked={monitoring} onChange={(e) => setMonitoring(e.target.checked)}
                  className="mt-1 h-4 w-4 text-[#1B2A4A] border-gray-300 rounded focus:ring-[#1B2A4A]"
                  data-testid="monitoring-checkbox" />
                <label htmlFor="monitoring" className="text-sm text-[#374151]">
                  <span className="font-medium">Monitoring Disclosure:</span> I understand that all activities on this system are monitored and recorded. I consent to security monitoring in accordance with federal security policies and FISMA compliance requirements.
                </label>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded px-4 py-3">
              <Lock size={14} className="text-blue-600 shrink-0" />
              <p className="text-xs text-blue-700">All data is encrypted at rest and in transit using FIPS 140-2 compliant encryption.</p>
            </div>

            <button
              onClick={handleInitialize}
              disabled={!consent || !monitoring || loading}
              data-testid="begin-onboarding-btn"
              className="w-full h-11 bg-[#1B2A4A] hover:bg-[#2D3F5F] text-white text-sm font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Initializing...' : 'Begin Secure Onboarding'}
            </button>
          </div>
        )}

        {/* Step 1: Identity Initialization */}
        {currentStep === 1 && (
          <div className="space-y-6" data-testid="phase-1">
            <div>
              <h2 className="text-xl font-semibold text-[#1B2A4A] mb-1" style={{ fontFamily: 'IBM Plex Sans' }}>Identity Initialization</h2>
              <p className="text-sm text-[#6B7280]">Provide your basic identity information. All data is protected under federal guidelines.</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded px-4 py-3">
              <p className="text-xs text-blue-700">
                <Lock size={12} className="inline mr-1" />
                Information collected is used for identity verification under federal compliance requirements (NIST 800-63).
              </p>
            </div>

            <div className="bg-white border border-[#E2E5E9] rounded p-5 space-y-4">
              <div>
                <label className={labelClass}>Full Legal Name <span className="text-red-500">*</span></label>
                <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="As it appears on government ID"
                  className={inputClass} data-testid="full-name-input" />
              </div>
              <div>
                <label className={labelClass}>Government Email</label>
                <input type="email" value={user?.email || ''} disabled className={`${inputClass} bg-gray-50 text-gray-500`} data-testid="gov-email-input" />
              </div>
              <div>
                <label className={labelClass}>Phone Number <span className="text-red-500">*</span></label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (202) 555-0100"
                  className={inputClass} data-testid="phone-input" />
              </div>
              <div>
                <label className={labelClass}>Agency / Department <span className="text-red-500">*</span></label>
                <select value={agency} onChange={(e) => setAgency(e.target.value)} className={selectClass} data-testid="agency-select">
                  <option value="">Select agency...</option>
                  {AGENCIES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>

            <button onClick={handlePhase1} disabled={loading} data-testid="phase1-submit-btn"
              className="w-full h-11 bg-[#1B2A4A] hover:bg-[#2D3F5F] text-white text-sm font-semibold rounded transition-colors disabled:opacity-50">
              {loading ? 'Submitting...' : 'Continue to Identity Verification'}
            </button>
          </div>
        )}

        {/* Step 2: Identity Assurance */}
        {currentStep === 2 && (
          <div className="space-y-6" data-testid="phase-2">
            <div>
              <h2 className="text-xl font-semibold text-[#1B2A4A] mb-1" style={{ fontFamily: 'IBM Plex Sans' }}>Identity Assurance (IAL2)</h2>
              <p className="text-sm text-[#6B7280]">Required to meet federal identity assurance standards (NIST 800-63).</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded px-4 py-3">
              <p className="text-xs text-amber-700">
                <Shield size={12} className="inline mr-1" />
                Your SSN is tokenized immediately and never stored in plain text. All PII is encrypted with FIPS 140-2 compliant algorithms.
              </p>
            </div>

            <div className="bg-white border border-[#E2E5E9] rounded p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>SSN (Last 4 digits) <span className="text-red-500">*</span></label>
                  <input type="password" maxLength={4} value={ssnLast4} onChange={(e) => setSsnLast4(e.target.value.replace(/\D/g, ''))}
                    placeholder="••••" className={inputClass} data-testid="ssn-input" />
                </div>
                <div>
                  <label className={labelClass}>Date of Birth <span className="text-red-500">*</span></label>
                  <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                    className={inputClass} data-testid="dob-input" />
                </div>
              </div>

              <div>
                <label className={labelClass}>Current Address <span className="text-red-500">*</span></label>
                <input type="text" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="Street address"
                  className={inputClass} data-testid="address1-input" />
              </div>
              <input type="text" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Apt, suite, unit (optional)"
                className={inputClass} data-testid="address2-input" />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>City <span className="text-red-500">*</span></label>
                  <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className={inputClass} data-testid="city-input" />
                </div>
                <div>
                  <label className={labelClass}>State <span className="text-red-500">*</span></label>
                  <select value={state} onChange={(e) => setState(e.target.value)} className={selectClass} data-testid="state-select">
                    <option value="">--</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>ZIP <span className="text-red-500">*</span></label>
                  <input type="text" maxLength={5} value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
                    className={inputClass} data-testid="zip-input" />
                </div>
              </div>

              <div>
                <label className={labelClass}>ID Document Type</label>
                <select value={docType} onChange={(e) => setDocType(e.target.value)} className={selectClass} data-testid="doc-type-select">
                  <option value="drivers_license">Driver's License</option>
                  <option value="passport">U.S. Passport</option>
                  <option value="military_id">Military ID</option>
                  <option value="piv_cac">PIV/CAC Card</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setCurrentStep(1)} className="h-11 px-6 border border-[#D1D5DB] rounded text-sm text-[#6B7280] hover:bg-gray-50">
                <ChevronLeft size={14} className="inline mr-1" />Back
              </button>
              <button onClick={handlePhase2} disabled={loading} data-testid="phase2-submit-btn"
                className="flex-1 h-11 bg-[#1B2A4A] hover:bg-[#2D3F5F] text-white text-sm font-semibold rounded transition-colors disabled:opacity-50">
                {loading ? 'Verifying...' : 'Verify Identity & Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Role & Clearance */}
        {currentStep === 3 && (
          <div className="space-y-6" data-testid="phase-3">
            <div>
              <h2 className="text-xl font-semibold text-[#1B2A4A] mb-1" style={{ fontFamily: 'IBM Plex Sans' }}>Role & Clearance Mapping</h2>
              <p className="text-sm text-[#6B7280]">Establish your access classification and security clearance level.</p>
            </div>

            <div className="bg-white border border-[#E2E5E9] rounded p-5 space-y-4">
              <div>
                <label className={labelClass}>Role <span className="text-red-500">*</span></label>
                <select value={role} onChange={(e) => setRole(e.target.value)} className={selectClass} data-testid="role-select">
                  <option value="federal_employee">Federal Employee</option>
                  <option value="contractor">Contractor</option>
                  <option value="administrator">Administrator</option>
                  <option value="executive">Executive</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Agency <span className="text-red-500">*</span></label>
                <select value={agency} onChange={(e) => setAgency(e.target.value)} className={selectClass} data-testid="phase3-agency-select">
                  <option value="">Select agency...</option>
                  {AGENCIES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Sub-Agency / Division</label>
                <input type="text" value={subAgency} onChange={(e) => setSubAgency(e.target.value)} placeholder="e.g., Bureau of Fiscal Service"
                  className={inputClass} data-testid="sub-agency-input" />
              </div>
              <div>
                <label className={labelClass}>Position Title</label>
                <input type="text" value={positionTitle} onChange={(e) => setPositionTitle(e.target.value)} placeholder="e.g., Financial Analyst"
                  className={inputClass} data-testid="position-title-input" />
              </div>
              <div>
                <label className={labelClass}>Security Clearance Level</label>
                <select value={clearance} onChange={(e) => setClearance(e.target.value)} className={selectClass} data-testid="clearance-select">
                  <option value="none">None</option>
                  <option value="public_trust">Public Trust</option>
                  <option value="secret">Secret</option>
                  <option value="top_secret">Top Secret</option>
                  <option value="sci">Top Secret / SCI</option>
                </select>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded px-4 py-3">
              <p className="text-xs text-blue-700">
                <Shield size={12} className="inline mr-1" />
                Access will be provisioned based on role and clearance level following least privilege principles (Zero Trust Architecture).
              </p>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setCurrentStep(2)} className="h-11 px-6 border border-[#D1D5DB] rounded text-sm text-[#6B7280] hover:bg-gray-50">
                <ChevronLeft size={14} className="inline mr-1" />Back
              </button>
              <button onClick={handlePhase3} disabled={loading} data-testid="phase3-submit-btn"
                className="flex-1 h-11 bg-[#1B2A4A] hover:bg-[#2D3F5F] text-white text-sm font-semibold rounded transition-colors disabled:opacity-50">
                {loading ? 'Processing...' : 'Submit for Review & Provision Access'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Under Review (auto-passes for demo) */}
        {currentStep === 4 && (
          <div className="text-center py-12" data-testid="phase-review">
            <FileCheck size={48} className="mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold text-[#1B2A4A] mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>Under Review</h2>
            <p className="text-sm text-[#6B7280] mb-6">Your application is being reviewed by the compliance team.</p>
            <div className="status-pending text-sm">UNDER REVIEW</div>
          </div>
        )}

        {/* Step 5: Complete */}
        {currentStep === 5 && (
          <div className="text-center py-12" data-testid="phase-complete">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#1B2A4A] mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>Access Provisioned</h2>
            <p className="text-sm text-[#6B7280] mb-2">Your identity has been verified and your retirement account has been created.</p>
            <p className="text-xs text-[#9CA3AF] mb-8">Identity Assurance Level: IAL2 | Authentication Assurance Level: AAL2</p>

            <div className="bg-white border border-[#E2E5E9] rounded p-5 max-w-sm mx-auto mb-6 text-left space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle size={14} className="text-emerald-600" />
                <span className="text-[#374151]">Identity verified (NIST 800-63 IAL2)</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle size={14} className="text-emerald-600" />
                <span className="text-[#374151]">Role & clearance assigned</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle size={14} className="text-emerald-600" />
                <span className="text-[#374151]">Compliance review passed</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle size={14} className="text-emerald-600" />
                <span className="text-[#374151]">Retirement account provisioned</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle size={14} className="text-emerald-600" />
                <span className="text-[#374151]">Zero Trust access enabled</span>
              </div>
            </div>

            <button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn"
              className="h-11 px-8 bg-[#1B2A4A] hover:bg-[#2D3F5F] text-white text-sm font-semibold rounded transition-colors">
              Access Your Dashboard
            </button>
          </div>
        )}

        {/* Footer legal */}
        <div className="mt-12 pt-6 border-t border-[#E2E5E9]">
          <p className="text-[10px] text-[#9CA3AF] text-center leading-relaxed">
            This system operates under the authority of the U.S. Office of Personnel Management. All activities are logged and subject to audit. Data handling complies with NIST SP 800-63-3, FISMA, and applicable federal regulations.
          </p>
        </div>
      </div>
    </div>
  );
};
